/**
 * FOV punch + hitstop zoom — transient camera FOV effects.
 *
 * FOV punch: a brief +N° FOV spike on kick, out-and-back over ~120 ms,
 * layered ON TOP of the rig's speed-based FOV. Applied after `rig.update`
 * by writing `camera.fov = baseline + delta` and re-calling
 * `updateProjectionMatrix`. The write is NON-ACCUMULATING: the baseline is
 * tracked explicitly rather than read back from the (already punched) camera,
 * so the delta can never ratchet even if the rig stops re-basing the FOV for
 * a while (e.g. while the crash cam owns the camera).
 *
 * Hitstop zoom: during the sim hitstop (detected by the sim phase check in
 * main.ts — the sim freezes ~90 ms), we apply a small FOV decrease (zoom-in)
 * to enhance the freeze frame. This is purely visual; the sim time advance
 * is unchanged.
 */

import { JUICE, easeOutAndBack, clamp01 } from "./juice";

const FP = JUICE.fovPunch;

interface FovPulse {
  elapsed: number;
  duration: number;
  magnitude: number; // deg (positive = widen, negative = zoom-in)
}

/**
 * FOV effect controller. Call `triggerKickPunch` from the kick event handler;
 * call `setHitstop(active)` when the sim enters/exits hitstop; call
 * `apply(camera, dt)` each render frame.
 */
export class FovEffects {
  private pulse: FovPulse | null = null;
  private hitstopActive = false;
  /** Baseline (un-punched) FOV the last delta was layered on top of. */
  private baseline = 0;
  /** The exact value we wrote to `cam.fov` last frame, or null if we left the
   *  camera at its baseline. Lets us tell "nobody else touched fov since our
   *  write" (→ re-use the stored baseline) from "the rig re-based it"
   *  (→ adopt the new value), so the delta NEVER compounds. */
  private lastWritten: number | null = null;

  triggerKickPunch(): void {
    this.pulse = {
      elapsed: 0,
      duration: FP.kickDuration,
      magnitude: FP.kickDeg,
    };
  }

  /** Toggle the hitstop zoom-in (called when sim is frozen post-kick). */
  setHitstop(active: boolean): void {
    this.hitstopActive = active;
  }

  /**
   * Advance + apply the FOV delta to a perspective camera.
   * @param cam  the rig's PerspectiveCamera (has `.fov` + `updateProjectionMatrix`).
   * @param dt   render-frame delta seconds.
   * @returns the FOV delta in degrees added this frame (for chaining).
   */
  apply(
    cam: { fov: number; updateProjectionMatrix: () => void },
    dt: number,
  ): number {
    // Strictly non-accumulating: resolve the baseline BEFORE adding a delta.
    // If cam.fov still holds exactly what we wrote last frame, no other owner
    // (normally CameraRig.update) has re-based it, so the true baseline is the
    // stored one — never the already-punched value. Otherwise the camera holds
    // a fresh base and we adopt it.
    const baseline =
      this.lastWritten !== null && cam.fov === this.lastWritten
        ? this.baseline
        : cam.fov;

    let delta = 0;

    if (this.pulse) {
      this.pulse.elapsed += dt;
      const p = clamp01(this.pulse.elapsed / this.pulse.duration);
      if (p >= 1) {
        this.pulse = null;
      } else {
        delta += easeOutAndBack(p) * this.pulse.magnitude;
      }
    }

    if (this.hitstopActive) {
      delta += JUICE.hitstop.zoomFovDeg;
    }

    this.baseline = baseline;
    if (delta !== 0) {
      this.lastWritten = baseline + delta;
      cam.fov = this.lastWritten;
      cam.updateProjectionMatrix();
    } else {
      // No delta: hand the camera back its clean baseline (undoes our last
      // write even when the rig is not re-basing, e.g. during the crash cam).
      this.lastWritten = null;
      if (cam.fov !== baseline) {
        cam.fov = baseline;
        cam.updateProjectionMatrix();
      }
    }
    return delta;
  }

  /** Whether any FOV effect is active. */
  get active(): boolean {
    return this.pulse !== null || this.hitstopActive;
  }

  reset(): void {
    this.pulse = null;
    this.hitstopActive = false;
    this.lastWritten = null;
    this.baseline = 0;
  }
}
